import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    BreadcrumbGroup,
    BreadcrumbGroupProps,
    Button,
    SideNavigationProps,
    SpaceBetween,
    TextContent,
} from '@cloudscape-design/components';
import '../styles/learning-detail-page.scss';
import { useArtifactDetails } from 'src/components/providers/ArticleDetailsProvider';
import { ArticleDetailsApiType } from 'src/types/useApi';
import { useApi } from 'src/hooks/useApi';
import { ArticleDetails } from 'src/types/learningDetails';
import { AppLayoutWrapper } from './AppLayoutWrapper';
import {
    buildDetailsSideNav,
    buildOrderedSubtopicList,
    buildHrefMap,
    findDuplicates,
} from 'src/utils/hierarchyUtils';
import { useTranslate } from 'src/i18n/useTranslate';
import { ContentObjects } from 'src/types/contentObject';
import { getLocaleDate } from 'src/helpers';
import { useFavorites } from 'src/components/providers/FavoritesProvider';
import { useAuth } from 'src/components/providers/AuthProvider';
import initialMetricsPublisher from 'src/metrics';
import * as KatalMetrics from '@amzn/katal-metrics';
import {
    NAV_GRAPH_ROOT_NODE_ID,
    USE_NAV_GRAPH_V2,
    useNavGraph,
} from 'src/components/providers/NavGraphProvider';
import {
    preferencesDefault,
    usePreferences,
} from 'src/components/providers/PreferencesProvider';
import {
    NOTIFICATIONS,
    useNotifications,
} from 'src/components/providers/NotificationsProvider';
import { useRecentlyViewed } from 'src/components/providers/RecentlyViewedProvider';
import { PreferenceLocation } from 'src/types/preferences';
import HotKeyButton from 'src/components/HotKeyButton';
import { HOTKEY_SHORTCUT_LABEL } from 'src/constants';
import { PREVIOUS_HOTKEY_SYMBOL } from 'src/constants';
import { NEXT_HOTKEY_SYMBOL } from 'src/constants';
import { useCallback, useMemo, useRef } from 'react';
import { getStage } from 'src/helpers';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import ArtifactDetail from 'src/components/ArtifactDetail/ArtifactDetail';
dayjs.extend(utc);

interface LearningArtifactDetailPageProps {
    setExploreModalOpen: (open: boolean) => void;
}

const LearningArtifactDetailPage = ({
    setExploreModalOpen,
}: LearningArtifactDetailPageProps) => {
    const stage = getStage();
    const navigate = useNavigate();
    const t = useTranslate();
    const location = useLocation();
    let params = useParams();

    const [subtopicIdsForPagination, setSubtopicIdsForPagination] = useState<
        string[]
    >([]);
    const [
        currentSubtopicIndexForPagination,
        setCurrentSubtopicIndexForPagination,
    ] = useState(0);
    const [sideNavStructure, setSideNavStructure] = useState<
        SideNavigationProps.Item[]
    >([]);
    const [hrefMap, setHrefMap] = useState<Map<string, string>>(new Map());
    const [duplicates, setDuplicates] = useState(new Set(''));
    const [duplicateSubtopics, setDuplicateSubtopics] = useState<string[]>([]);
    const { hash } = location;
    // remove "#/" from beginning of hash and optional appended slug
    const contentObjectLcmsXmlGUID = hash.substring(2).split('/')[0];

    let artifactId = params?.artifactId;

    // Learning artifact detail breadcrumbs cannot open the explore modal via click,
    // therefore we wait for the link to include "explore=true" and then read from the param on the hash
    // however, since URLSearchParams don't work after a hash, we just have to look for the string value on the hash itself
    useEffect(() => {
        if (location.hash.includes('explore=true')) {
            setExploreModalOpen(true);
        }
    }, [location.hash]);
    const {
        breadcrumbs,
        name: articleName,
        dateUpdated: dateArticleUpdated,
    } = useArtifactDetails();

    const [artifactData, setArtifactData] = useState<ArticleDetails>();

    const { addFavorite, deleteFavorite, isFavorite } = useFavorites();

    const { getBreadcrumbNamesFromPath, getPathSegmentsByDocumentId } =
        useNavGraph();

    const { user } = useAuth();

    const { userPreferences } = usePreferences();
    const { publishNotification, dismissNotification } = useNotifications();
    const { addRecentlyViewed } = useRecentlyViewed();

    const {
        makeRequest: getArticleDetails,
        data: articleData,
        loading: articleDataLoading,
        error: articleDataError,
    }: ArticleDetailsApiType = useApi({
        url: `learning-artifacts/${artifactId}`,
        method: 'GET',
    });

    const crumbsFromNavGraph = getBreadcrumbNamesFromPath(
        articleData?.classifications.find((classification) =>
            classification.path.startsWith(`${NAV_GRAPH_ROOT_NODE_ID}/`)
        )?.path
    );

    // v2: try to get pathSegments from nav graph, fall back to classification path
    const pathSegments =
        USE_NAV_GRAPH_V2 && articleData?.id
            ? getPathSegmentsByDocumentId(articleData.id)
            : undefined;

    const classificationPath = articleData?.classifications.find(
        (classification) =>
            classification.path.startsWith(`${NAV_GRAPH_ROOT_NODE_ID}/`)
    )?.path;

    const currFullPath = pathSegments
        ? pathSegments.map((seg: { id: string }) => seg.id)
        : classificationPath?.split('/').filter(Boolean).slice(1);

    // v2: build cumulative paths for breadcrumb navigation
    const breadcrumbPaths =
        USE_NAV_GRAPH_V2 && currFullPath
            ? currFullPath.map((_: any, i: number) =>
                  currFullPath.slice(0, i + 1).join('/')
              )
            : currFullPath;

    const breadcrumbsWithHome = (
        breadcrumbs?.length
            ? [
                  { text: 'Home', href: '/' },
                  ...breadcrumbs.map((b, i) => {
                      return {
                          text: b,
                          href: breadcrumbPaths
                              ? `${hash}?explore=true&tab=${breadcrumbPaths[i]}`
                              : undefined,
                      };
                  }),
              ]
            : crumbsFromNavGraph?.length
            ? [
                  { text: 'Home', href: '/' },
                  ...crumbsFromNavGraph.map((b, i) => {
                      return {
                          text: b,
                          href: breadcrumbPaths
                              ? `${hash}?explore=true&tab=${breadcrumbPaths[i]}`
                              : undefined,
                      };
                  }),
              ]
            : []
    ) as BreadcrumbGroupProps.Item[];

    const documentMetricsPublisher = useMemo(() => {
        return initialMetricsPublisher.newChildActionPublisherForMethod(
            'Document'
        );
    }, []);

    const lastGuid = useRef<string | null>(null);
    useEffect(() => {
        // do not emit when subtopic hasn't changed or when subtopic hasn't been added to path yet
        //   (this usually happens on first page load)
        if (
            lastGuid.current === contentObjectLcmsXmlGUID ||
            !contentObjectLcmsXmlGUID ||
            !articleData?.name
        ) {
            return;
        }
        // also don't emit when subtopic title hasn't been added to path yet
        //   (component will rerender a 2nd time with subtopic title which could result in duplicated metrics being emitted)
        if (hash.split('/').length <= 2) {
            return;
        }
        const topicName = decodeURIComponent(hash.split('/')[2].split('?')[0]);
        lastGuid.current = contentObjectLcmsXmlGUID;

        const metricSubtopicId = contentObjectLcmsXmlGUID;
        const completeTopic =
            findTopicPath(articleData?.contentObjects, metricSubtopicId) ||
            t('none', 'None');
        const utcTimestamp = `${dayjs().utc().valueOf()}-${
            (Math.random() * 1000000) | 0
        }`;
        const hashedUserId = user?.hashedUserId || '';
        const publisherName =
            (articleData?.latestVersion as any)?.publisherName || '';
        const documentVersion = (articleData?.latestVersion as any)?.name || '';

        const commonkey = `${utcTimestamp} - ${stage} hashed userId: ${
            hashedUserId || ''
        }`;
        const document_id = `${commonkey} - documentId: ${artifactId}`;
        const topicId = `${commonkey} - topicId: ${metricSubtopicId}`;
        const artifact_title = `${commonkey} - artifactTitle: ${articleData?.name}`;
        const complete_topic = `${commonkey} - complete_topic: ${completeTopic}`;
        const topic_name = `${commonkey} - topicName: ${topicName}`;
        const url = `${commonkey} - url: ${window.document.location.href}`;
        const doc_version = `${commonkey} - documentVersion: ${
            documentVersion || ''
        }`;
        const publisher_name = `${commonkey} - publisherName: ${publisherName}`;

        documentMetricsPublisher.publish(
            new KatalMetrics.Metric.String('gru.document', commonkey)
        );
        documentMetricsPublisher.publish(
            new KatalMetrics.Metric.String('gru.document', doc_version)
        );
        documentMetricsPublisher.publish(
            new KatalMetrics.Metric.String('gru.document', publisher_name)
        );
        documentMetricsPublisher.publish(
            new KatalMetrics.Metric.String('gru.document', url)
        );
        documentMetricsPublisher.publish(
            new KatalMetrics.Metric.String('gru.document', document_id)
        );
        documentMetricsPublisher.publish(
            new KatalMetrics.Metric.String('gru.document', artifact_title)
        );
        documentMetricsPublisher.publish(
            new KatalMetrics.Metric.String('gru.document', complete_topic)
        );
        documentMetricsPublisher.publish(
            new KatalMetrics.Metric.String('gru.document', topic_name)
        );
        documentMetricsPublisher.publish(
            new KatalMetrics.Metric.String('gru.document', topicId)
        );
    }, [contentObjectLcmsXmlGUID, articleData?.name, hash]);

    const findTopicPath = (
        contentObjects: any,
        topicID: string
    ): string | null => {
        let output: string | null = null;

        const hasTopicPath = (topics: any[], currentPath: string): boolean => {
            if (!Array.isArray(topics)) {
                return false;
            }

            for (const topic of topics) {
                if (!topic) {
                    continue;
                }

                const topicPath = currentPath
                    ? `${currentPath} : ${topic.name}`
                    : topic.name;

                if (topic.lcmsXmlGUID === topicID) {
                    output = topicPath;
                    return true;
                }

                if (
                    topic.topics &&
                    Array.isArray(topic.topics) &&
                    topic.topics.length > 0
                ) {
                    if (hasTopicPath(topic.topics, topicPath)) {
                        return true;
                    }
                }
            }

            return false;
        };

        if (
            !contentObjects?.lessons ||
            !Array.isArray(contentObjects.lessons)
        ) {
            return null;
        }

        for (const lesson of contentObjects.lessons) {
            if (!lesson?.topics || !Array.isArray(lesson.topics)) {
                continue;
            }

            if (hasTopicPath(lesson.topics, lesson.name)) {
                break;
            }
        }
        return output;
    };

    useEffect(() => {
        getArticleDetails({
            url: `learning-artifacts/${artifactId}`,
        });
    }, [artifactId]);

    useEffect(() => {
        if (articleDataError) {
            navigate('/not-found');
        }
    }, [articleDataError]);

    useEffect(() => {
        if (!articleName || !dateArticleUpdated || !contentObjectLcmsXmlGUID) {
            setSubtopicIdsForPagination([]);
        }
    }, []);

    const navigateToFirstSubtopicIfNoneSelected = (
        newSideNavStructure: SideNavigationProps.Item[],
        newSubtopicIds: string[],
        hrefMap: Map<string, string>
    ) => {
        const getIdOfFirstChildWithoutItems = (navSection: any): string => {
            // if section has items, search through first item to see if it also has items
            if (navSection.items?.length) {
                return getIdOfFirstChildWithoutItems(navSection?.items[0]);
                // if section does not have items, return navSection href which is `#/${lcmsXmlGUID}`
            } else {
                return navSection.href;
            }
        };

        // if we have content objects and don't currently have a sub-topic selected
        // change URL to have a sub-topic
        if (newSideNavStructure.length && !contentObjectLcmsXmlGUID) {
            const idOfFirstChildWithoutItems = getIdOfFirstChildWithoutItems(
                newSideNavStructure[0]
            );
            navigate(idOfFirstChildWithoutItems, { replace: true });
            setCurrentSubtopicIndexForPagination(
                newSubtopicIds.indexOf(idOfFirstChildWithoutItems)
            );
        } else {
            if (contentObjectLcmsXmlGUID) {
                setCurrentSubtopicIndexForPagination(
                    subtopicIdsForPagination.indexOf(contentObjectLcmsXmlGUID)
                );
                if (hrefMap.get(contentObjectLcmsXmlGUID)) {
                    navigate(hrefMap.get(contentObjectLcmsXmlGUID)!, {
                        replace: true,
                    });
                    // Don't enter path if there's already a full location in the URL
                } else if (
                    location.hash
                        .split('/')
                        .filter((str: string) => str !== '' && str !== '#')
                        .length < 2
                ) {
                    const results = [];
                    for (const key of hrefMap.keys()) {
                        if (key.includes(contentObjectLcmsXmlGUID)) {
                            results.push(key);
                        }
                    }
                    if (results.length) {
                        // Get first
                        results.sort();
                        navigate(hrefMap.get(results[0])!, {
                            replace: true,
                        });
                    }
                }
            }
        }
    };

    const navigateToNotFoundIfInvalidSubtopicSelected = (
        subtopicIds: string[]
    ) => {
        /*
            if we have a sectionId in url, and we have built subtopics
            if subtopics don't include sectionId go to not found
        */
        if (contentObjectLcmsXmlGUID && subtopicIds.length) {
            const guidPattern =
                /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
            const subtopicIsGuid = guidPattern.test(contentObjectLcmsXmlGUID);
            if (
                !subtopicIsGuid ||
                !subtopicIds.includes(contentObjectLcmsXmlGUID)
            ) {
                navigate('/not-found');
                return true;
            }
        }
    };

    const isDocumentWrongContent = (): boolean => {
        const artifactClassificationPaths = articleData?.classifications?.map(
            (classification) => classification.path
        );
        if (!artifactClassificationPaths?.length) {
            return true;
        }

        const userPreferenceKeys = Object.keys(userPreferences);
        if (!userPreferenceKeys.length) {
            return false;
        }

        if (userPreferences.language.name == 'English (US)') {
            if (
                !['English', 'English (United States)'].includes(
                    articleData.languages
                )
            ) {
                return true;
            }
        } else if (userPreferences.language.name == 'French (France)') {
            if (articleData.languages !== 'French') {
                return true;
            }
        } else if (userPreferences.language.name !== articleData.languages) {
            return true;
        }

        for (const userPreferenceKey of userPreferenceKeys) {
            if (
                !['role', 'site', 'country', 'region'].includes(
                    userPreferenceKey
                )
            )
                continue;

            if (userPreferenceKey === 'site') {
                const siteClassificationPaths =
                    artifactClassificationPaths.filter(
                        (path) =>
                            path.startsWith('187') &&
                            path.split('/').filter(Boolean).length === 4
                    );
                if (!siteClassificationPaths.length) continue;
            }

            const preferenceLocation: PreferenceLocation =
                userPreferences[
                    userPreferenceKey as 'role' | 'site' | 'country' | 'region'
                ]!;
            if (userPreferenceKey == 'role' || userPreferenceKey == 'site') {
                if (
                    preferenceLocation?.path !==
                        preferencesDefault[userPreferenceKey]?.path &&
                    !artifactClassificationPaths.includes(
                        preferenceLocation?.path
                    )
                )
                    return true;
            } else {
                if (
                    !artifactClassificationPaths.includes(
                        preferenceLocation.path
                    )
                )
                    return true;
            }
        }

        return false;
    };

    useEffect(() => {
        if (USE_NAV_GRAPH_V2) {
            return;
        }
        dismissNotification(NOTIFICATIONS.ARTIFACT_ROLE_WARNING);
        if (articleData && isDocumentWrongContent()) {
            publishNotification({
                type: 'warning',
                id: NOTIFICATIONS.ARTIFACT_ROLE_WARNING,
                content: (
                    <>
                        {t(
                            'artifact_preferences_warning',
                            "It looks like this document isn't a good match for your preferences."
                        )}
                    </>
                ),
            });

            const wrongContentPublisher =
                initialMetricsPublisher.newChildActionPublisherForMethod(
                    'WrongContent'
                );
            wrongContentPublisher.publish(
                new KatalMetrics.Metric.String(
                    'gru.wrongContent.click',
                    articleData.id.toString()
                )
            );
        }
    }, [articleData, userPreferences, t]);

    useEffect(() => {
        if (articleData) {
            setArtifactData(articleData);

            const foundDuplicates = new Set(
                findDuplicates(articleData.contentObjects?.lessons)
            );
            const builtSideNavStructure = buildDetailsSideNav(
                articleData.contentObjects?.lessons || [],
                foundDuplicates
            );
            const builtHrefMap = buildHrefMap(
                articleData.contentObjects?.lessons || [],
                foundDuplicates
            );
            setSideNavStructure(builtSideNavStructure);
            setHrefMap(builtHrefMap);
            setDuplicates(foundDuplicates);
            const { subtopicIds, duplicateSubtopics } =
                buildOrderedSubtopicList(articleData);
            setSubtopicIdsForPagination(subtopicIds);
            setDuplicateSubtopics(duplicateSubtopics);

            navigateToFirstSubtopicIfNoneSelected(
                builtSideNavStructure,
                subtopicIds,
                builtHrefMap
            );
            navigateToNotFoundIfInvalidSubtopicSelected(subtopicIds);

            const topicName = hash.split('/')[2]?.split('?')[0];
            const decodedTopicName = topicName
                ? decodeURIComponent(topicName)
                : undefined;
            const topicId = contentObjectLcmsXmlGUID;
            addRecentlyViewed(articleData.id, topicId, decodedTopicName);
        }
    }, [articleData, hash, contentObjectLcmsXmlGUID]);

    const dateUpdated = getLocaleDate(
        dateArticleUpdated || artifactData?.dateUpdated
    );

    const onSubtopicSelection = (
        subtopicDetails: CustomEvent<SideNavigationProps.FollowDetail>
    ) => {
        const elements = subtopicDetails.detail.href.split('/');
        const topicId = elements[1];
        if (duplicates.has(topicId)) {
            const id = subtopicDetails.detail.href.split('/')[2].split('-')[0];
            const lookup = `${id}-${topicId}`;
            setCurrentSubtopicIndexForPagination(
                duplicateSubtopics.indexOf(lookup)
            );
        } else {
            setCurrentSubtopicIndexForPagination(
                subtopicIdsForPagination.indexOf(
                    subtopicDetails.detail.href.substring(2)
                )
            );
        }
    };

    const updateCurrentSubtopic = useCallback(
        (newGuid: string) => {
            if (newGuid && subtopicIdsForPagination.length) {
                // for duplicates, extract the id from the hash to find the correct index
                const hashParts = hash.split('/');
                const topicIdentifier = hashParts[2]?.split('?')[0];

                // check if this is a duplicate (has id- prefix)
                if (
                    topicIdentifier &&
                    topicIdentifier.includes('-') &&
                    !topicIdentifier.match(/^[0-9a-f-]+$/i)
                ) {
                    const id = topicIdentifier.split('-')[0];
                    const lookupKey = `${id}-${newGuid}`;
                    const newIndex = duplicateSubtopics.indexOf(lookupKey);
                    if (
                        newIndex !== -1 &&
                        newIndex !== currentSubtopicIndexForPagination
                    ) {
                        setCurrentSubtopicIndexForPagination(newIndex);
                    }
                    return;
                }

                // Find the index of the newGuid, starting from the current index
                let newIndex = subtopicIdsForPagination.indexOf(
                    newGuid,
                    currentSubtopicIndexForPagination
                );

                // If not found, search from the beginning
                if (newIndex === -1) {
                    newIndex = subtopicIdsForPagination.findIndex((val) =>
                        val.includes(newGuid)
                    );
                }

                // Only update if the index actually changed
                if (
                    newIndex !== -1 &&
                    newIndex !== currentSubtopicIndexForPagination
                ) {
                    setCurrentSubtopicIndexForPagination(newIndex);
                }
            }
        },
        [
            subtopicIdsForPagination,
            currentSubtopicIndexForPagination,
            duplicateSubtopics,
            hash,
        ]
    );

    type SideNavItem = {
        text: string;
        href: string;
        items: SideNavItem[];
    };
    const findHrefIfNotFound = (items: SideNavItem[], sectionId: string) => {
        for (let item of items) {
            //@ts-ignore
            if (item.href?.includes(sectionId)) {
                return item;
            }

            if (item.items?.length) {
                findHrefIfNotFound(item.items, sectionId);
            }
        }
    };

    const getHref = (newIndex: number) => {
        const targetSubtopicId = subtopicIdsForPagination[newIndex];
        const href = hrefMap.get(targetSubtopicId.toString());
        if (href) {
            return href;
        } else {
            const duplicateTargetSubtopicId = duplicateSubtopics[newIndex];
            let href = hrefMap.get(duplicateTargetSubtopicId.toString());
            if (!href && duplicateTargetSubtopicId) {
                const guid = duplicateTargetSubtopicId
                    .split('-')
                    .slice(1)
                    .join('-');
                href = hrefMap.get(guid);
            }
            return href ?? '';
        }
    };
    const goToHref = (newIndex: number) => {
        const href = getHref(newIndex);
        navigate(href);
        setCurrentSubtopicIndexForPagination(newIndex); // Directly set the index
    };
    const goToPreviousSubtopic = () => {
        if (currentSubtopicIndexForPagination > 0) {
            const newIndex = currentSubtopicIndexForPagination - 1;
            goToHref(newIndex);
        }
    };

    const goToNextSubtopic = () => {
        if (
            currentSubtopicIndexForPagination <
            subtopicIdsForPagination.length - 1
        ) {
            const newIndex = currentSubtopicIndexForPagination + 1;
            goToHref(newIndex);
        }
    };

    useEffect(() => {
        if (contentObjectLcmsXmlGUID) {
            updateCurrentSubtopic(contentObjectLcmsXmlGUID);
        }
    }, [contentObjectLcmsXmlGUID, updateCurrentSubtopic]);

    const clickFavoriteButtonMetricsPublisher =
        initialMetricsPublisher.newChildActionPublisherForMethod(
            'ClickFavoriteButton'
        );
    const clickFavoriteButton = (id: number) => {
        const topicName = hash.split('/')[2]?.split('?')[0];
        const decodedTopicName = topicName
            ? decodeURIComponent(topicName)
            : undefined;
        const topicId = contentObjectLcmsXmlGUID;

        isFavorite(id)
            ? deleteFavorite(id)
            : addFavorite(id, topicId, decodedTopicName);

        const documentMetricKey = isFavorite(id)
            ? 'gru.favorite.add.document'
            : 'gru.favorite.remove.document';
        const userMetricKey = isFavorite(id)
            ? 'gru.favorite.add.user'
            : 'gru.favorite.remove.user';

        clickFavoriteButtonMetricsPublisher.publish(
            new KatalMetrics.Metric.String(documentMetricKey, id.toString())
        );
        clickFavoriteButtonMetricsPublisher.publish(
            new KatalMetrics.Metric.String(
                userMetricKey,
                user?.hashedUserId || ''
            )
        );
    };

    const getFavoriteText = (artifactDataId: number) =>
        isFavorite(artifactDataId)
            ? t('remove-from-favorites', 'Remove from Favorites')
            : t('add-to-favorites', 'Add to Favorites');

    const DetailsPage = (
        <div>
            <SpaceBetween size="s">
                <div className="title-container">
                    <TextContent data-details-title>
                        <h1>{articleName || artifactData?.name}</h1>
                    </TextContent>
                </div>
                <div className="last-modified-container">
                    <TextContent>
                        <small>
                            {t(
                                'details-page-last-modified',
                                'Last modified on'
                            )}{' '}
                            <small data-last-modified-date>{dateUpdated}</small>
                        </small>
                    </TextContent>
                    <div className="icon-container">
                        {artifactData?.id && (
                            <div
                                style={{
                                    transformOrigin: 'center center',
                                    display: 'inline-block',
                                }}
                            >
                                <Button
                                    ariaLabel={getFavoriteText(artifactData.id)}
                                    iconName="heart"
                                    variant="normal"
                                    data-heart-icon-filled={isFavorite(
                                        artifactData.id
                                    )}
                                    data-testid={
                                        isFavorite(artifactData.id)
                                            ? 'filled-heart'
                                            : 'empty-heart'
                                    }
                                    data-heart-icon
                                    onClick={() => {
                                        clickFavoriteButton(artifactData.id);
                                    }}
                                >
                                    {getFavoriteText(artifactData.id)}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </SpaceBetween>

            <ArtifactDetail
                artifactId={artifactId ?? ''}
                contentObjectLcmsXmlGUID={contentObjectLcmsXmlGUID}
            />

            <div
                className="button-container"
                style={{
                    justifyContent: 'flex-end',
                    display: 'flex',
                    gap: '16px',
                }}
            >
                <HotKeyButton
                    disabled={!(currentSubtopicIndexForPagination > 0)}
                    onClick={() => goToPreviousSubtopic()}
                    data-testid="details-previous-button"
                    hotKeyIdentifier="ArrowLeft"
                >
                    {t('annotation-context-previous', 'Previous')} (
                    {HOTKEY_SHORTCUT_LABEL}{' '}
                    <span style={{ fontSize: '13px' }}>
                        {PREVIOUS_HOTKEY_SYMBOL}
                    </span>
                    )
                </HotKeyButton>
                <HotKeyButton
                    disabled={
                        !(
                            currentSubtopicIndexForPagination <
                            subtopicIdsForPagination.length - 1
                        )
                    }
                    onClick={() => goToNextSubtopic()}
                    variant="primary"
                    data-testid="details-next-button"
                    hotKeyIdentifier="ArrowRight"
                >
                    {t('annotation-context-next', 'Next')} (
                    {HOTKEY_SHORTCUT_LABEL}{' '}
                    <span style={{ fontSize: '13px' }}>
                        {NEXT_HOTKEY_SYMBOL}
                    </span>
                    )
                </HotKeyButton>
            </div>
        </div>
    );

    return (
        <SpaceBetween size="s" data-details-page>
            <div className="details-breadcrumbs-and-icons-container">
                <div className="breadcrumbs-container">
                    <BreadcrumbGroup
                        items={breadcrumbsWithHome}
                        ariaLabel={t('breadcrumbs', 'Breadcrumbs')}
                    />
                </div>
            </div>
            <AppLayoutWrapper
                content={DetailsPage}
                sideNavStructure={sideNavStructure}
                hrefMap={hrefMap}
                onSubtopicSelection={onSubtopicSelection}
                isDetailsPage
            />
        </SpaceBetween>
    );
};

export default LearningArtifactDetailPage;
